import {
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query'

import { ExecutionResult } from 'graphql'
import { TypedDocumentString } from './gql/graphql'
import env from './constants/env'
import useAuthStore from './stores/useAuthStore'

/**
 * `graphql()` (generated in `src/gql/gql.ts`) looks documents up by their exact
 * source string and falls back to `{}` when the document isn't in the generated
 * types. That object stringifies to "[object Object]", so the request would be
 * sent with a bogus query and the caller would just get no data - the page then
 * shows its empty state as if there were simply nothing to show.
 *
 * Guard against that: fail loudly with the fix instead.
 */
const LOOKS_LIKE_GRAPHQL = /^\s*(query|mutation|subscription|fragment)\b|^\s*[{(]/

const assertGeneratedDocument = (query: string) => {
  if (!LOOKS_LIKE_GRAPHQL.test(query)) {
    return Promise.reject(
      new Error(
        'GraphQL document not found in the generated types. Run `npm run codegen` (and restart the dev server), then reload the page.',
      ),
    )
  }
  return null
}

const fetcher = <TResult, TVariables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables?: TVariables,
  headers?: Record<string, string>,
) => {
  return () => {
    const query = String((document as unknown as object) ?? '')
    const invalid = assertGeneratedDocument(query)
    if (invalid) return invalid

    return fetch(`${env.graphqlUrl}`, {
      method: 'POST',
      credentials: 'include', // Send cookie to server
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    })
      .then((response) => response.json())
      .then((response) => {
        const { data, errors } = response
        if (errors?.length) {
          return Promise.reject(new Error(errors[0].message))
        }
        return data
      }) as Promise<ExecutionResult<TResult>>
  }
}

export function useQueryGraphQL<TResult, TVariables>(
  queryKeys: unknown[],
  document: TypedDocumentString<TResult, TVariables>,
  variables?: TVariables,
  options?: UseQueryOptions<TResult, any, TResult>,
  headers?: Record<string, string>,
) {
  const { getOrgSlug } = useAuthStore()
  const orgType = localStorage.getItem('org-type')
  return useQuery(
    [...queryKeys, variables] as const,
    // @ts-ignore
    fetcher(document, variables, {
      'thrive-org': getOrgSlug(),
      'org-type': orgType || '',
      ...headers,
    }),
    options,
  )
}

export const getNextPageParam = ({ allPages, totalCount, limit }): number | undefined => {
  if (allPages.length * limit >= totalCount || 0) {
    return undefined
  }
  return allPages.length + 1
}

export function useInfiniteQueryGraphQL<TResult, TVariables>(
  queryKeys: unknown[],
  document: TypedDocumentString<TResult, TVariables>,
  variables?: TVariables,
  options?: UseQueryOptions<TResult, any, TResult>,
  headers?: Record<string, string>,
) {
  const { getOrgSlug } = useAuthStore()
  const orgType = localStorage.getItem('org-type')
  // @ts-ignore
  const limit = variables?.pagination?.limit

  return useInfiniteQuery(
    [...queryKeys, variables] as const,
    // @ts-ignore
    ({ pageParam = 1 }) =>
      // @ts-ignore
      fetcher(
        document,
        // @ts-ignore
        { ...variables, pagination: { limit: limit, offset: limit * (pageParam - 1) } },
        {
          'thrive-org': getOrgSlug(),
          'org-type': orgType || '',
          ...headers,
        },
      )(),
    options,
  )
}

export function useMutationGraphQL<TResult, TVariables>(
  document: TypedDocumentString<TResult, TVariables>,
  variables: TVariables,
  options?: UseMutationOptions,
  headers?: Record<string, string>,
) {
  const { getOrgSlug } = useAuthStore()
  const orgType = localStorage.getItem('org-type')
  return useMutation(
    fetcher(document, variables, {
      'thrive-org': getOrgSlug(),
      'org-type': orgType || '',
      ...headers,
    }),
    options,
  )
}
